/* eslint-disable @typescript-eslint/no-explicit-any */
import '@patsnap-ui/icon/assets/solid/ChartChunk.svg'
import { ConversationChatType, CreateConversationParams } from '@patsnap/ls360_sdk_feature'
import { ILang } from '@patsnap/synapse_common_interface'
import { useAuthStore, useLocale } from '@pharmsnap/shared/src/composition'
import { sharedCtx } from '@pharmsnap/shared/src/context'
import { trackingEvent } from '@pharmsnap/shared/src/features/tracking'
import { CopilotCommand } from '@pharmsnap/shared/src/types'
import { CHAT_DIALOG_ID } from '@pharmsnap/shared/src/utils/business/ls360'
import { computed, getCurrentInstance, inject, nextTick, provide, ref, watch } from '@vue/composition-api'
import { useLocalStorage } from '@vueuse/core'
import { openNewTab, setLocalStorage, sleep } from 'pharmsnapMF_shared/utils'
import { getLS360QuestionExamples, QuestionDataSourceType } from '../components/LS360/util'
import { VUE_APP_EUREKA_URL, VUE_APP_LS360_URL } from '../config'
import router from '../router'
import { E_ROUTER_NAME } from '../types/enum'
import { createChatUUid } from '../utils/business/ls360'
import { query2Base64String } from '../utils/business/search'
import { useLs360SynapseWujieEvents } from './useLs360SynapseWujieEvents'

type EmailAlertStatusPayload = {
  /** 是否支持设置邮件提醒 */
  support: boolean
  /** 是否已经设置了邮件提醒 */
  hasSetAlert?: boolean
  /** 邮件提醒是否打开 */
  opened?: boolean
  /** 不支持原因 */
  notSupportReason?: string
  /** 不支持icon tooltip提示 */
  notSupportTooltip?: string
}

const getChatType = (command: CopilotCommand): ConversationChatType => {
  switch (command) {
    case CopilotCommand.explain: {
      return 'explain'
    }
    case CopilotCommand.translate: {
      return 'translate'
    }
    case CopilotCommand.summarize: {
      return 'summarize'
    }
    case CopilotCommand.page_summarize: {
      return 'page_summarize'
    }
    default:
      return 'normal'
  }
}

// 问题项接口
interface QuestionItem {
  question: string
  dataSource: QuestionDataSourceType
  questionType?: 'normal' | 'page_summarize'
}

// 问题状态管理接口
interface QuestionState {
  currentQuestions: QuestionItem[]
  generalQuestions: QuestionItem[]
}

// 为不同页面类型配置特定问题
const PAGE_SPECIAL_QUESTION_CONFIG: Partial<Record<E_ROUTER_NAME, (locale: ILang, pageContext?: any) => string>> = {
  [E_ROUTER_NAME.DRUG]: (locale, pageContext) => {
    const drugName =
      (locale === 'CN' ? pageContext?.params?.display_name_cn : pageContext?.params?.display_name_en) || pageContext?.params?.display_name_en
    return locale === 'CN' ? `总结[${drugName}]这个药物的信息。` : `Please help me summarize the drug [${drugName}].`
  },
  [E_ROUTER_NAME.DISEASE]: (locale, pageContext) => {
    const diseaseName =
      (locale === 'CN' ? pageContext?.params?.display_name_cn : pageContext?.params?.display_name_en) || pageContext?.params?.display_name_en
    return locale === 'CN' ? `总结[${diseaseName}]这个疾病的信息。` : `Please help me summarize the disease [${diseaseName}].`
  },
  [E_ROUTER_NAME.ORGANIZATION]: (locale, pageContext) => {
    const orgName =
      (locale === 'CN' ? pageContext?.params?.display_name_cn : pageContext?.params?.display_name_en) || pageContext?.params?.display_name_en
    return locale === 'CN' ? `总结[${orgName}]这个机构的信息。` : `Please help me summarize the organization [${orgName}].`
  },
  [E_ROUTER_NAME.TARGET]: (locale, pageContext) => {
    const targetName =
      (locale === 'CN' ? pageContext?.params?.display_name_cn : pageContext?.params?.display_name_en) || pageContext?.params?.display_name_en
    return locale === 'CN' ? `总结[${targetName}]这个靶点的信息。` : `Please help me summarize the target [${targetName}].`
  },
  [E_ROUTER_NAME.DRUG_TYPE]: (locale, pageContext) => {
    const drugTypeName =
      (locale === 'CN' ? pageContext?.params?.display_name_cn : pageContext?.params?.display_name_en) || pageContext?.params?.display_name_en
    return locale === 'CN' ? `总结[${drugTypeName}]这个药物类型的信息。` : `Please help me summarize the drug type [${drugTypeName}].`
  },
  [E_ROUTER_NAME.DRUG_LIST]: (locale) => {
    return locale === 'CN' ? '总结当前药物列表。' : 'Summarize this drug list.'
  },
  [E_ROUTER_NAME.DRUG_DEAL_LIST]: (locale) => {
    return locale === 'CN' ? '总结当前药物交易列表。' : 'Summarize this deal list.'
  },
  [E_ROUTER_NAME.PATENT_LIST]: (locale) => {
    return locale === 'CN' ? '总结当前专利列表。' : 'Summarize this patent list.'
  },
}
export const ASSISTANT_DIALOG_VISIBLE_KEY = 'ASSISTANT_DIALOG_VISIBLE'
const LS360_SYMBOL = Symbol('LS360_SYMBOL')

// 这个不是 hook... 这里不要用其他 vue hook，要不会有很多问题
export const useLs360Provider = () => {
  const dialogVisible = useLocalStorage<boolean>(ASSISTANT_DIALOG_VISIBLE_KEY, false, {
    writeDefaults: true,
  })
  const { localeUpcase, tsText } = useLocale()
  const localStorageReasoningFlag = useLocalStorage<boolean>('__ls360_reasoning_flag__', true)
  const { bus } = useLs360SynapseWujieEvents()
  const ins = getCurrentInstance()

  const {
    getters: { isOutSeaHiroUser },
  } = useAuthStore()

  const route = computed(() => ins?.root.proxy.$route)
  /** 弹窗是否被渲染 */
  const renderDialog = ref(false)
  /** isPrinting: 理解问题 or 打字中 */
  const isPrinting = ref(false)
  /** assistant 是否挂载 */
  const mounted = ref(false)
  /** 返回顶部的按钮 right 距离 */
  const copilotDialogRightDistanceToViewport = ref(0)
  /** pageContext 页面总结用 */
  const pageContext = ref<any>()
  const summarizeCallback = ref<() => void>()

  // 问题状态管理
  const questionState = ref<QuestionState>({
    currentQuestions: [],
    generalQuestions: [],
  })
  /** 当前页面是否支持设置邮件提醒,以及不支持原因 */
  const emailAlertStatus = ref<EmailAlertStatusPayload>({
    support: true,
    hasSetAlert: false,
    opened: false,
  })
  /**
   * 初始化邮件提醒状态
   * @returns
   */
  function initEmailAlertStatus(): EmailAlertStatusPayload {
    if (!router.currentRoute)
      return {
        support: false,
        notSupportReason: 'router.currentRoute is undefined',
      }
    const { name } = router.currentRoute
    if (!name) {
      return {
        support: false,
        notSupportReason: 'router.currentRoute.name is undefined',
      }
    }
    const canSetEmailAlertListPageNames: E_ROUTER_NAME[] = [
      E_ROUTER_NAME.DRUG_LIST,
      E_ROUTER_NAME.DRUG_DEAL_LIST,
      E_ROUTER_NAME.CLINICAL_PROGRESS_LIST,
      E_ROUTER_NAME.PATENT_LIST,
      E_ROUTER_NAME.TRANSLATIONAL_MEDICINE_LIST,
      E_ROUTER_NAME.DRUG_GROUP_BY_DISEASE,
      E_ROUTER_NAME.DRUG_GROUP_BY_DEV_ORG,
      E_ROUTER_NAME.DRUG_GROUP_BY_TARGET,
      E_ROUTER_NAME.DRUG_GROUP_BY_DRUG_TYPE,
    ]
    const canSetEmailAlertDetailPageNames: E_ROUTER_NAME[] = [
      E_ROUTER_NAME.DRUG,
      E_ROUTER_NAME.CLINICAL_DETAIL,
      E_ROUTER_NAME.DISEASE,
      E_ROUTER_NAME.ORGANIZATION,
      E_ROUTER_NAME.TARGET,
      E_ROUTER_NAME.DRUG_TYPE,
      E_ROUTER_NAME.PATENT_DETAIL,
    ]
    if (![...canSetEmailAlertListPageNames, ...canSetEmailAlertDetailPageNames].includes(name as E_ROUTER_NAME)) {
      const notSupport = tsText('抱歉，当前页面不支持设置邮件提醒。', 'Sorry, the current page does not support setting email alerts.')
      return {
        support: false,
        notSupportReason: notSupport,
        notSupportTooltip: notSupport,
      }
    }
    if (canSetEmailAlertListPageNames.includes(name as E_ROUTER_NAME)) {
      const tab = router.currentRoute.query.tab
      if (tab === 'analysis') {
        const notSupport = tsText('抱歉，当前页面不支持设置邮件提醒。', 'Sorry, the current page does not support setting email alerts.')
        return {
          support: false,
          notSupportReason: notSupport,
          notSupportTooltip: notSupport,
        }
      }
    }
    return emailAlertStatus.value
  }
  const updateDistance = () => {
    const copilotElement = document.getElementById(CHAT_DIALOG_ID)
    copilotDialogRightDistanceToViewport.value = copilotElement?.getBoundingClientRect()?.width || 0
  }
  function addWujieEventListener() {
    bus.$on('micro-ls360-synapse:share-chat', async (payload) => {
      // 海外Hiro LS 用户，跳转到 Hiro LS
      const targetUrl = isOutSeaHiroUser.value
        ? `${VUE_APP_LS360_URL}/chat/${payload.chatId}`
        : `${VUE_APP_EUREKA_URL}/ai-ls/chat-agent/${payload.chatId}?from=synapse`
      const url = new URL(targetUrl)
      url.searchParams.set('action', 'open_share')
      if (payload.chatItemId) {
        url.searchParams.set('chat_item_id', payload.chatItemId)
      }
      trackingEvent('GOTO_LS360', {
        common1: payload.chatId,
        trigger_point: 'SIDE_BAR_SHARE',
      })
      window.open(url.toString(), '_blank')
    })
    bus.$on('micro-ls360-synapse:deep-chat', async (payload) => {
      const chatId = createChatUUid()
      const deepSearchPreviousItemId = payload.deepSearchPreviousItemId

      const newQuery = payload.newQuery

      const requestParams: CreateConversationParams = {
        chat_id: chatId,
        chat_type: 'normal',
        data_source: 'all',
        query: newQuery,
        deep_search_previous_item_id: deepSearchPreviousItemId,
        prompt: false,
        reasoning_flag: payload.reasoningFlag,
      }

      const res = isOutSeaHiroUser.value
        ? await sharedCtx.service.hiroLsApi.createConversation(undefined, {
            data: requestParams,
          })
        : await sharedCtx.service.ls360Api.createConversation(undefined, {
            data: requestParams,
          })

      if (res.success && res.data && res.data.chat_id) {
        const url = isOutSeaHiroUser.value
          ? new URL(`${VUE_APP_LS360_URL}/chat/search/${res.data.chat_id}`)
          : new URL(`${VUE_APP_EUREKA_URL}/ai-ls/chat-agent/${res.data.chat_id}?from=synapse`)

        trackingEvent('GOTO_LS360', {
          common1: res.data.chat_id,
          trigger_point: 'SIDE_BAR_DEEP_SEARCH',
        })
        window.open(url.toString(), '_blank')
      }
    })
  }
  addWujieEventListener()
  const updateMicroPageContext = () => {
    bus.$emit('main-synapse:update-page-summarize-context', pageContext.value)
  }

  // 问题管理actions
  const questionActions = {
    /**
     * 初始化通用问题
     */
    async initGeneralQuestions() {
      const questions = await getLS360QuestionExamples(localeUpcase.value)
      // getLS360QuestionExamples返回的是各个source的问题，generalQuestions赋值为每个source的前3条
      questionState.value.generalQuestions = questions
      questionState.value.currentQuestions = questionState.value.generalQuestions
      return questions
    },

    /**
     * 更新当前页面问题
     */
    updateCurrentPageQuestions(pageType: E_ROUTER_NAME, pageContext?: any) {
      // 通过检查是否配置了问题来判断是否为特殊页面
      const isSpecial = !!PAGE_SPECIAL_QUESTION_CONFIG[pageType]

      let newQuestions: QuestionItem[]
      if (isSpecial) {
        // 特殊页面：使用通用问题，但最后一个dataSource为all的问题替换成特殊问题
        const generalQuestions = [...questionState.value.generalQuestions]
        const specialQuestion = PAGE_SPECIAL_QUESTION_CONFIG[pageType]?.(localeUpcase.value, pageContext) || ''

        // 找到最后一个dataSource为all的问题的索引
        const lastAllIndex = generalQuestions
          .map((q, index) => (q.dataSource === 'all' ? index : -1))
          .filter((index) => index !== -1)
          .pop()

        if (lastAllIndex !== undefined) {
          // 替换最后一个dataSource为all的问题
          generalQuestions[lastAllIndex] = {
            question: specialQuestion,
            dataSource: 'all' as QuestionDataSourceType,
            questionType: 'page_summarize',
          }
        }

        newQuestions = generalQuestions
      } else {
        // 非特殊页面：使用通用问题
        newQuestions = questionState.value.generalQuestions
      }

      questionState.value.currentQuestions = newQuestions

      // 通知微前端更新问题，只传递exampleList
      bus.$emit('main-synapse:update-example-question', {
        exampleList: newQuestions,
      })
    },

    /**
     * 重置为通用问题
     */
    resetToGeneralQuestions() {
      questionState.value.currentQuestions = questionState.value.generalQuestions

      // 通知微前端更新问题，只传递exampleList
      bus.$emit('main-synapse:update-example-question', {
        exampleList: questionState.value.generalQuestions,
      })
    },

    /**
     * 获取当前问题列表
     */
    getCurrentQuestions() {
      return questionState.value.currentQuestions
    },
  }

  const actions = {
    setReasoningFlag(flag: boolean) {
      // if (IS_CN_REGION) {
      //   localStorageReasoningFlag.value = flag
      // }
      localStorageReasoningFlag.value = flag
    },
    getReasoningFlag() {
      // if (IS_CN_REGION) {
      //   return localStorageReasoningFlag.value
      // }
      // return false

      return localStorageReasoningFlag.value
    },
    setWujieMounted() {
      mounted.value = true
    },
    awaitWujieMounted: async () => {
      actions.showAssistantDialog(true)
      // 轮询等 LS360SiderBarDialog 加载完
      await Promise.race([
        (async () => {
          while (!mounted.value) {
            await sleep(100)
          }
        })(),
        sleep(3000),
      ])
    },
    scrollToBottom: () => {
      // TODO:
      // bus.$on()
    },
    showAssistantDialog: (visible: boolean) => {
      renderDialog.value = true
      dialogVisible.value = visible
      setLocalStorage(ASSISTANT_DIALOG_VISIBLE_KEY, visible.toString())
      nextTick(() => updateDistance())
    },
    setPrinting: (printing = false) => {
      isPrinting.value = printing
    },
    handleSelectEmitCommand: async (command: CopilotCommand, selectedContent: string, isResend = false) => {
      await actions.awaitWujieMounted()
      if (command === CopilotCommand.start_chat) {
        bus.$emit('main-synapse:start-chat', selectedContent)
        return
      }
      if (command === CopilotCommand.start_chat_sent_immediately) {
        const req: Partial<CreateConversationParams> = {
          query: selectedContent,
          chat_type: getChatType(command),
        }
        bus.$emit('main-synapse:start-underline-chat', req)
        return
      }
      if (command === CopilotCommand.page_summarize) {
        const req: Partial<CreateConversationParams> = {
          query: selectedContent,
          chat_type: getChatType(command),
          page_context: actions.getPageContext(),
        }
        bus.$emit('main-synapse:start-underline-chat', req)
        return
      }

      const req: Partial<CreateConversationParams> = {
        query: selectedContent,
        chat_type: getChatType(command),
        page_context: {
          page_name: route.value?.name || '',
          page_url: window.location.href || '',
          params: {
            language: localeUpcase.value,
            content: selectedContent,
          },
        },
      }
      bus.$emit('main-synapse:start-underline-chat', req)
    },
    gotoSearchResult(options: any) {
      const { baseUrl, query, newTab = true, dataType = 'all', collapse } = options
      const queryParam = {
        data_type: dataType,
        query,
      }
      if (collapse) {
        Object.assign(queryParam, { collapse })
      }
      const url = new URL(baseUrl)
      url.searchParams.append('query', query2Base64String(queryParam))
      // 添加这个标识是为了告诉网关将流量打到正式版而不是游客版
      url.searchParams.append('login_success', 'true')
      if (newTab) {
        openNewTab(url.toString())
      } else {
        window.location.href = url.toString()
      }
    },
    /** 设置页面总结参数 */
    setPageContext(
      // pageContextParams: Omit<PageContext['params'], 'language'>,
      // ctx?: Partial<Omit<PageContext, 'params'> & { callback?: () => void }>
      pageContextParams: any,
      ctx?: any
    ) {
      const { page_url = window.location.href || '', page_name = route.value?.name || '', callback } = ctx || {}
      pageContext.value = {
        page_name,
        page_url,
        params: {
          ...(pageContextParams as any),
          language: localeUpcase.value,
        },
      }
      summarizeCallback.value = callback
      updateMicroPageContext()

      // 如果是特殊页面，更新问题列表
      if (page_name && PAGE_SPECIAL_QUESTION_CONFIG[page_name as E_ROUTER_NAME]) {
        questionActions.updateCurrentPageQuestions(page_name as E_ROUTER_NAME, pageContext.value)
      }
    },
    /** 获取页面总结参数 */
    getPageContext() {
      return pageContext.value
    },
    /** 清空页面总结参数 */
    clearPageContext() {
      pageContext.value = undefined
      summarizeCallback.value = undefined
      updateMicroPageContext()
    },
    onCallbackSummarize() {
      summarizeCallback.value?.()
    },
    position2Anchor(anchor: string) {
      if (!anchor) return
      const anchorElement = document.getElementById(anchor)
      // Scroll to the anchor element
      anchorElement && anchorElement.scrollIntoView()
    },
    /**
     * 更新邮件提醒状态
     * @param payload
     */
    updateEmailAlertStatus: (payload: EmailAlertStatusPayload) => {
      emailAlertStatus.value = payload
      bus.$emit('main-synapse:update-email-alert-status', emailAlertStatus.value)
    },
    initEmailAlertStatus,
  }

  watch(
    () => route.value?.name,
    (newVal, oldVal) => {
      if (newVal !== oldVal) {
        actions.clearPageContext()

        // 更新页面问题
        if (newVal) {
          questionActions.updateCurrentPageQuestions(newVal as E_ROUTER_NAME, pageContext.value)
        }
      }
    },
    {
      immediate: true,
    }
  )
  const res = {
    state: {
      emailAlertStatus,
      dialogVisible,
      renderDialog,
      isPrinting,
      pageContext,
      copilotDialogRightDistanceToViewport,
    },
    actions: {
      ...actions,
      questionActions,
    },
  }
  provide(LS360_SYMBOL, res)
  return res
}

export const useLs360ChatStore = () => {
  const chatStore = inject(LS360_SYMBOL)
  return chatStore as ReturnType<typeof useLs360Provider>
}
